import { FormikProps } from 'formik'
import { ibanToBic } from 'iban-to-bic'
import { BIC, IBAN } from 'ibankit'

// see https://stackoverflow.com/a/5219948
export const EMAIL_REGEX = /[^ ]+@[^ ]+\.[^ ]{2,6}/
export const SLUG_REGEX = /^[A-Za-z0-9-]{3,}$/
export const PHONE_REGEX = /^\+?[0-9\s-]+$/
export const LINKED_IN_PROFILE_URL_REGEX = /^https:\/\/www\.linkedin\.com\/in\/(\w|-)+\/?$/

export function isEmail(input: string) {
    return EMAIL_REGEX.test(input)
}

export const UNICODE_EMAIL_REGEX =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i

// UNICODE_EMAIL_REGEX but allows multiple comma separated emails
export const UNICODE_EMAILS_REGEX =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})(,\s*(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,}))*$/i

const INVITE_EMAIL_SEPARATOR_REGEX = /[,;\n\r\t]+/
const ZERO_WIDTH_CHARACTERS_REGEX = /[\u200b\ufeff]/g

function normalizeInviteEmailToken(rawEmail: string): string {
    const trimmed = rawEmail.trim().replace(ZERO_WIDTH_CHARACTERS_REGEX, '')
    const angleBracketMatch = trimmed.match(/<([^>]+)>/)
    return (angleBracketMatch?.[1] ?? trimmed).trim().replace(ZERO_WIDTH_CHARACTERS_REGEX, '')
}

/**
 * Splits a raw invite-emails field value into trimmed, deduplicated email addresses.
 * Accepts comma, semicolon, tab, and newline separators to tolerate pasted lists from
 * spreadsheets or address books, strips zero-width characters, extracts addresses from
 * "Name <email>" tokens, and removes case-insensitive duplicates.
 */
export function parseInviteEmails(rawEmails: string): string[] {
    const seenLowerCase = new Set<string>()
    const emails: string[] = []

    for (const rawEmail of rawEmails.split(INVITE_EMAIL_SEPARATOR_REGEX)) {
        const email = normalizeInviteEmailToken(rawEmail)
        if (!email) continue

        const lowerCaseEmail = email.toLowerCase()
        if (seenLowerCase.has(lowerCaseEmail)) continue

        seenLowerCase.add(lowerCaseEmail)
        emails.push(email)
    }

    return emails
}

export function isFaulty(formik: FormikProps<any>, fieldId: string) {
    return formik.touched[fieldId] && formik.errors[fieldId]
}

export function formatZipInput(value: string): string {
    return value.replace(/\D/g, '').slice(0, 5)
}

export function isValidDachZip(zip: string): boolean {
    const germanZipRegex = /^\d{5}$/
    const swissZipRegex = /^\d{4}$/
    const australianZipRegex = /^\d{4}$/
    return germanZipRegex.test(zip) || swissZipRegex.test(zip) || australianZipRegex.test(zip)
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
    return PHONE_REGEX.test(phoneNumber)
}

export function isValidLinkedInProfileUrl(url: string): boolean {
    return LINKED_IN_PROFILE_URL_REGEX.test(url)
}

export function isValidIBAN(value: string, allowEmpty = false) {
    try {
        if (!value && allowEmpty) return true
        // Remove spaces for validation
        const cleanValue = value.replace(/\s/g, '')
        return IBAN.isValid(cleanValue)
    } catch {
        return false
    }
}

export function isValidBIC(value: string, allowEmpty = false) {
    try {
        if (!value && allowEmpty) return true
        return BIC.isValid(value)
    } catch {
        return false
    }
}

export function resolveBicFromIban(iban: string): string | null {
    try {
        // Remove spaces before resolving BIC
        const cleanIban = iban.replace(/\s/g, '')
        return ibanToBic(cleanIban)
    } catch {
        // Silently fail - this is just a helper feature
        return null
    }
}

/**
 * Formats an IBAN with spaces every 4 characters for better readability
 * Example: DE89370400440532013000 → DE89 3704 0044 0532 0130 00
 */
export function formatIban(value: string): string {
    // Remove all spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, '').toUpperCase()
    // Add space every 4 characters
    return cleaned.match(/.{1,4}/g)?.join(' ') || cleaned
}

/**
 * Removes all spaces from IBAN for storage/validation
 * Example: DE89 3704 0044 0532 0130 00 → DE89370400440532013000
 */
export function unformatIban(value: string): string {
    return value.replace(/\s/g, '').toUpperCase()
}
